import React, { lazy, Suspense } from 'react';
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import '../styles/SNTAM.css';

// ✨ OPTIMIZATION 1: Use React.lazy for code splitting.
// These components will now only be downloaded by the browser when they're needed.
const BlogList = lazy(() => import('../components/blog/BlogList'));
const SingleBlog = lazy(() => import('../components/blog/SingleBlog'));

// ✨ OPTIMIZATION 2: Define static constants outside the component.
// This prevents them from being redeclared on every render.
const siteUrl = "https://robertfalkenberg.com";
const blogListPageTitle = "Blog | Standing Next To A Mountain - Robert Falkenberg";
const blogListPageDescription = "Explore articles, insights, and stories from author Robert Falkenberg on the 'Standing Next To A Mountain' blog.";
const blogListUrl = `${siteUrl}/standing-next-to-a-mountain`; // Assuming this is the correct base URL

// JSON-LD data can also be prepared outside
const blogListJsonLd = {
  "@context": "https://schema.org",
  "@type": "CollectionPage",
  "name": blogListPageTitle,
  "description": blogListPageDescription,
  "url": blogListUrl + '/',
  "publisher": {
    "@type": "Person",
    "name": "Robert Falkenberg",
    "url": siteUrl
  }
};

const SNTAM = () => {
  const location = useLocation();

  // This logic correctly determines if we're on the main list page.
  const isBlogListPage = !location.pathname.includes('/blogDetails/');

  return (
    <div className="SNTAM-container">
      {isBlogListPage && (
        <Helmet>
          {/* Core SEO Meta Tags */}
          <title>{blogListPageTitle}</title>
          <meta name="description" content={blogListPageDescription} />
          <link rel="canonical" href={blogListUrl + '/'} />

          {/* Open Graph Tags */}
          <meta property="og:type" content="website" />
          <meta property="og:url" content={blogListUrl + '/'} />
          <meta property="og:title" content={blogListPageTitle} />
          <meta property="og:description" content={blogListPageDescription} />
          <meta property="og:site_name" content="Robert Falkenberg Author" />
          <meta property="og:locale" content="en_US" />

          {/* Twitter Card Tags */}
          <meta name="twitter:card" content="summary" />
          <meta name="twitter:title" content={blogListPageTitle} />
          <meta name="twitter:description" content={blogListPageDescription} />

          {/* JSON-LD Structured Data */}
          <script type="application/ld+json">
            {JSON.stringify(blogListJsonLd)}
          </script>
        </Helmet>
      )}

      <div className="blog-container">
        {/* ✨ OPTIMIZATION 3: Wrap Routes in Suspense to handle lazy loading. */}
        <Suspense fallback={<div className="blog-loading-container"><div className="blog-loader"></div></div>}>
          <Routes>
            <Route path="/" element={<BlogList />} />
            <Route path="blogDetails/:id" element={<SingleBlog />} />
          </Routes>
        </Suspense>
      </div>
    </div>
  );
};

// ✨ OPTIMIZATION 4: Memoize the component to prevent unnecessary re-renders.
export default React.memo(SNTAM);